import DocumentToolNav from '@/components/DocumentToolNav';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import AppHeader from './AppHeader';
import ContactSlideOver from './ContactSlideOver';
import TrialStatusBanner from './TrialStatusBanner';
import { CATEGORIES } from '@/lib/toolCategories';
import { useDesktopFileDrop } from '@/hooks/useDesktopFileDrop';

/**
 * Map route pathname → section title shown in AppHeader.
 * Matches mockup `.header .h-title` labels.
 */
function sectionTitle(pathname) {
  if (pathname === '/tools') return 'Home';
  if (pathname === '/recent') return 'Recent files';
  if (pathname.startsWith('/launch/')) {
    const cat = pathname.slice('/launch/'.length);
    if (!cat || cat === 'all') return 'All tools';
    const found = CATEGORIES.find((c) => c.id === cat);
    return found ? found.label : 'Tools';
  }
  if (pathname.startsWith('/account/billing')) return 'Plan & billing';
  if (pathname === '/account/profile') return 'Edit profile';
  if (pathname === '/account/devices') return 'License & devices';
  if (pathname.startsWith('/account')) return 'Account & settings';
  if (pathname === '/cloud') return 'Cloud';
  return '';
}

/**
 * DesktopShell — the Electron window body (sidebar + main column).
 *
 * Reproduces the mockup `.window .body` area (everything BELOW the
 * faux `.titlebar` — the real Electron frame supplies the traffic lights
 * and title bar).
 *
 * Root element carries `scrivar-desktop` which activates IBM Plex font
 * and --ds-* design tokens from `src/styles/desktop-shell.css`.
 */
export default function DesktopShell() {
  const location = useLocation();
  const title = sectionTitle(location.pathname);
  // "Drag a file anywhere in this window" — window-wide PDF drop → viewer.
  useDesktopFileDrop();

  return (
    <div
      className="scrivar-desktop"
      style={{
        display: 'flex',
        height: '100vh',
        overflow: 'hidden',
      }}
    >
      {/* Left sidebar */}
      <Sidebar />

      {/* Main column: header + scrollable content area */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <TrialStatusBanner />
        <AppHeader variant="app" title={title} />
        <DocumentToolNav />

        {/* Scrollable page content — matches mockup `.scroll` */}
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            padding: '28px 32px 36px',
          }}
        >
          <Outlet />
        </div>
      </div>

      {/* Contact slide-over (stub — filled in Task 5.2) */}
      <ContactSlideOver />
    </div>
  );
}
